import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Clock, Flame, Bell, Repeat, Pencil } from "lucide-react";
import Countdown from "../Countdown";
import PriorityChip from "./PriorityChip";
import { recurrenceShort } from "./helpers";

// The chips row under an item title: recurrence indicator, priority chip,
// date chip, "mochi's pick" / "recently focused" badges, shame-safe status
// label, time chip with inline editor, countdown, alert chip, streak flame.
// Shared pop-in motion for status chips (stable reference).
const CHIP_POP_MOTION = {
    initial: { opacity: 0, scale: 0.85 },
    animate: { opacity: 1, scale: 1 },
    transition: { type: "spring", stiffness: 300, damping: 18 },
};

export default function RowChips({
    testId, completed, avatarStyle,
    recurrence, priority, onEditPriority, dateChip,
    mochiPick, recentlyFocused, statusLabel,
    time, onEditTime, showCountdown,
    onEditLead, currentLead, openLeadPicker,
    streak,
}) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(time || "");
    const inputRef = useRef(null);

    useEffect(() => {
        if (editing && inputRef.current) {
            inputRef.current.focus();
            // Most mobile browsers ignore .select() on time inputs but it's harmless.
            try { inputRef.current.select?.(); } catch { /* ignore */ }
        }
    }, [editing]);

    const commit = async () => {
        if (!draft || draft === time) { setEditing(false); return; }
        try { await onEditTime?.(draft); } catch (e) { console.warn("time edit failed (parent shows toast)", e); }
        setEditing(false);
    };
    const cancel = () => { setDraft(time || ""); setEditing(false); };

    return (
        <div className="flex flex-wrap items-center gap-2 mt-0.5">
            {recurrence && recurrence.type && recurrence.type !== "none" && (
                <span
                    className={`inline-flex items-center gap-1 text-[10px] font-display uppercase tracking-wider px-2 py-0.5 rounded-full ${
                        avatarStyle === "masculine"
                            ? "bg-indigo-100 text-indigo-600"
                            : "bg-rose-100 text-rose-600"
                    }`}
                    data-testid={`${testId}-recurring-indicator`}
                    title={`Repeats every ${recurrenceShort(recurrence)}`}
                    aria-label={`Repeats every ${recurrenceShort(recurrence)}`}
                >
                    <Repeat size={10} strokeWidth={2.4} />
                    {recurrenceShort(recurrence)}
                </span>
            )}
            <PriorityChip
                testId={testId}
                priority={priority}
                completed={completed}
                onEditPriority={onEditPriority}
            />
            {dateChip && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase font-display tracking-wider px-2 py-0.5 rounded-full bg-warm-300/30 text-warm-800">
                    {dateChip}
                </span>
            )}
            {mochiPick && !completed && (
                <motion.span
                    {...CHIP_POP_MOTION}
                    className="inline-flex items-center gap-1 text-[10px] uppercase font-display tracking-wider px-2 py-0.5 rounded-full bg-mustard text-warm-900 border border-mustard/40"
                    data-testid={`${testId}-mochi-pick`}
                    title="Mochi suggested this one"
                >
                    mochi's pick 🍡
                </motion.span>
            )}
            {recentlyFocused && !completed && (
                <motion.span
                    {...CHIP_POP_MOTION}
                    className="inline-flex items-center gap-1 text-[10px] uppercase font-display tracking-wider px-2 py-0.5 rounded-full bg-terracotta-soft/60 text-terracotta border border-terracotta/20"
                    data-testid={`${testId}-recently-focused`}
                    title="You ran a focus session on this recently"
                >
                    <span aria-hidden="true">🌟</span>
                    Recently focused
                </motion.span>
            )}
            {statusLabel && (
                <span
                    className="inline-flex items-center text-[10px] uppercase font-display tracking-wider px-2 py-0.5 rounded-full bg-warm-200/60 text-warm-700"
                    data-testid={`${testId}-status-label`}
                >
                    {statusLabel}
                </span>
            )}
            {!statusLabel && time && !editing && (
                <span className="inline-flex items-center gap-1 text-xs text-warm-500 font-body">
                    <Clock size={12} /> {time}
                    {onEditTime && !completed && (
                        <button
                            onClick={(e) => { e.stopPropagation(); setDraft(time); setEditing(true); }}
                            className="ml-0.5 p-0.5 rounded-full text-warm-500 hover:text-terracotta hover:bg-surface transition-colors"
                            data-testid={`${testId}-edit-time`}
                            aria-label="Edit time"
                            title="Edit time"
                        >
                            <Pencil size={11} />
                        </button>
                    )}
                </span>
            )}
            {editing && (
                <span
                    className="inline-flex items-center gap-1 text-xs text-warm-900 font-body bg-mustard-soft/60 rounded-full px-2 py-0.5"
                    data-testid={`${testId}-time-editor`}
                >
                    <Clock size={12} />
                    <input
                        ref={inputRef}
                        type="time"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onBlur={commit}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") commit();
                            if (e.key === "Escape") cancel();
                        }}
                        className="bg-transparent outline-none font-body text-xs w-[64px] tabular-nums"
                        data-testid={`${testId}-time-input`}
                        aria-label="New reminder time"
                    />
                </span>
            )}
            {showCountdown && time && (
                <Countdown time={time} completed={completed} />
            )}
            {onEditLead && !completed && (currentLead ?? 15) > 0 && (
                <button
                    onClick={(e) => { e.stopPropagation(); openLeadPicker(); }}
                    className="inline-flex items-center gap-1 text-[10px] uppercase font-display tracking-wider px-2 py-0.5 rounded-full bg-sage-soft/60 text-warm-900 hover:bg-sage-soft transition-colors"
                    data-testid={`${testId}-alert-chip`}
                    title="Edit alert"
                    aria-label={`Alert ${currentLead ?? 15} minutes before — tap to edit`}
                >
                    <Bell size={10} strokeWidth={2.5} />
                    {currentLead ?? 15}m
                </button>
            )}
            {streak > 0 && (
                <span className="inline-flex items-center gap-1 text-xs text-terracotta font-display">
                    <Flame size={12} /> {streak}
                </span>
            )}
        </div>
    );
}
